import {type Concession, type Region, type Site} from 'client';
import {type IFilter} from './action';

export function filterSites({
  filter,
  sites,
  regions,
}: {
  filter: Pick<IFilter, 'regions' | 'sites' | 'siteGroups'>;
  sites: Site[];
  regions: Region[];
}): Site[] {
  const applyFilter = applySiteFilterHandler({
    regions,
    sites,
    selectedSiteGroups: filter.siteGroups,
    selectedSites: filter.sites,
    selectedRegions: filter.regions,
  });

  return sites.filter((site) => filterSite({regions, site, filter, applyFilter}));
}

export function filterSite({
  regions,
  site,
  filter,
  applyFilter,
}: {
  regions: Region[];
  site: Site;
  filter: Pick<IFilter, 'regions' | 'sites' | 'siteGroups'>;
  applyFilter: IApplyFilter;
}): boolean {
  const {sites, siteGroups} = filter;
  if (sites.length > 0 && applyFilter.applySite) {
    return sites.includes(site.name);
  }
  if (siteGroups.length > 0 && applyFilter.applySiteGroup) {
    const filteredTags = siteGroups.filter((siteGroup) => site.tags?.includes(siteGroup));
    return filteredTags.length > 0;
  }

  if (filter.regions.length > 0 && applyFilter.applyRegion) {
    return filterByRegionId({siteConcession: site, filter, regions, applyFilter});
  }

  return true;
}

const filterByRegionId = ({
  siteConcession,
  filter,
  regions,
  applyFilter,
}: {
  siteConcession: Site | Concession;
  filter: Pick<IFilter, 'regions'>;
  regions: Region[];
  applyFilter: IApplyFilter;
}): boolean => {
  if (filter.regions.length > 0 && applyFilter.applyRegion) {
    return filter.regions.includes(getSiteConcessionRegionName(siteConcession, regions));
  }
  return true;
};

export function applySiteFilterHandler({
  regions,
  selectedRegions,
  sites,
  selectedSites,
  selectedSiteGroups,
}: {
  regions: Region[];
  selectedRegions: string[];
  sites: Site[];
  selectedSites: string[];
  selectedSiteGroups: string[];
}): IApplyFilter {
  const siteGroupFilter = () => {
    if (selectedSiteGroups && selectedSiteGroups?.length > 0) {
      const allTags = sites.map((site) => site.tags).flat();
      const allFilteredTags = selectedSiteGroups.filter((siteGroup) => allTags?.includes(siteGroup));
      return allFilteredTags.length !== 0;
    }
    return false;
  };

  const sitesFilter = () => {
    if (selectedSites && selectedSites?.length > 0) {
      const allSiteNames = sites.map((site) => site.name);
      const allFilteredSiteName = selectedSites.filter((siteName) => allSiteNames?.includes(siteName));
      return allFilteredSiteName.length !== 0;
    }
    return false;
  };

  const regionsFilter = () => {
    if (selectedRegions && selectedRegions?.length > 0) {
      const allRegionsNames = regions.map((region) => region.name);
      const allFilteredRegionsName = selectedRegions.filter((regionName) => allRegionsNames?.includes(regionName));
      return allFilteredRegionsName.length !== 0;
    }
    return false;
  };

  const applySiteGroup = siteGroupFilter();
  const applySite = sitesFilter();
  const applyRegion = regionsFilter();

  return {
    applySiteGroup,
    applySite,
    applyRegion,
    applySiteFilter: applySiteGroup || applySite || applyRegion,
  };
}

export const listFiltering = (selected: string[], selection: string): string[] => {
  if (selected?.includes(selection)) {
    return selected.filter((value) => value !== selection);
  } else {
    return [...selected, selection];
  }
};

const getSiteConcessionRegionName = (siteConcession: Site | Concession, regions: Region[]): string =>
  regions.find((region) => region.id === siteConcession.regionId)?.name ?? '';

export const uniqueSiteGroups = (sites: Site[]) =>
  Array.from(
    new Set(
      sites
        ?.map((site) => site.tags ?? [])
        .flat()
        .filter((tag) => tag !== undefined),
    ),
  );

interface IApplyFilter {
  applySiteGroup: boolean;
  applySite: boolean;
  applyRegion: boolean;
  applySiteFilter: boolean;
}
